import * as React from 'react'
import moment from 'moment'
import { Tooltip, Icon } from 'antd'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../stores/CurrentUserStore'
import { POSStore } from '../../stores/POSStore'
import {
  API,
  IPayment,
  paymentNameFormatter,
  PaymentStatus,
  PermissionNames,
} from '@getgreenline/homi-shared'
import { Currency } from '../../components/Currency'
import { getFullNotes, transactionsIncomplete } from '../../utilities/helpers'
import { ExpandablePaymentDetails } from './ExpandablePaymentDetails'
import { BLAZE_COMPANY } from '../../constants/general'
import { Skeleton } from 'antd-v4'
import { LoyaltyDiscountUtils, LoyaltyModels } from '@getgreenline/loyalties'
import { BlazepayEnums, BlazepayPaymentOptionModels } from '@getgreenline/payments'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
  posStore?: POSStore // Passed in from the POS (not injected), unlocks refunding functionality
  currentEmployeeId: string | null // Passed in from the Dashboard, unlocks editing the payment type
  payment: IPayment
  onUpdate?: () => void
  canEdit: boolean
  canRefund: boolean
  hideButtons?: boolean
  canEditCustomer: boolean
  paymentOptions?: Map<
    BlazepayEnums.Provider,
    BlazepayPaymentOptionModels.IPaymentOptionEventOrRestData
  >
}

interface State {
  expanded?: boolean
  errorMessage?: string
  showRefundModal: boolean
  showCompleteModal: boolean
  showCancelModal: boolean
  showChangeModal: boolean
  refundPayment: IPayment | undefined
  originalSalePayment: IPayment | undefined
  isLoading: boolean
  loyaltyData?: LoyaltyModels.ILoyaltyData
}

// NOTE: When updating the columns of this component, make sure to update the headers of the following:
// - DashboardPayments/index.tsx
// - PaymentListModal.tsx

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class ExpandablePayment extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      expanded: false,
      showRefundModal: false,
      showCompleteModal: false,
      showCancelModal: false,
      showChangeModal: false,
      refundPayment: undefined,
      originalSalePayment: undefined,
      isLoading: false,
    }
  }

  setInProgress() {
    if (window.confirm('Are you sure?')) {
      const payment = this.props.payment
      this.props
        .currentCompanyStore!.inProgressPayment(payment.locationId, payment.id)
        .then(() => {
          this.onUpdate()
        })
        .catch((error) => {
          this.setState({
            errorMessage: error.response.data.message || 'There was an error updating this sale',
          })
        })
    }
  }

  onUpdate() {
    if (this.props.onUpdate) {
      this.props.onUpdate()
    }
  }

  onClick = async () => {
    const { currentCompanyStore, payment } = this.props
    const expanded = !this.state.expanded
    let refundPayment = this.state.refundPayment
    let originalSalePayment = this.state.originalSalePayment
    let loyaltyData = this.state.loyaltyData

    if (payment.refundPaymentId) {
      if (originalSalePayment === undefined && expanded) {
        originalSalePayment = await API.getPaymentById(
          currentCompanyStore!.company!.id!,
          payment.refundPaymentId,
        )
      }
    } else if (refundPayment === undefined && expanded) {
      refundPayment = await API.getRefundedPayment(currentCompanyStore!.company!.id!, payment.id)
    }

    if (
      currentCompanyStore &&
      currentCompanyStore.company?.permissions.canAccessLoyalty &&
      this.state.loyaltyData === undefined &&
      expanded
    ) {
      const { canDisplayLoyaltyInfoOnReceipt, canUseCustomReceipt, customReceiptSettings } =
        currentCompanyStore
      currentCompanyStore.getShouldDisplayInternalLoyaltyData(payment.locationId)

      loyaltyData = currentCompanyStore.shouldDisplayInternalLoyaltyData
        ? await LoyaltyDiscountUtils.getLoyaltyData(
            currentCompanyStore.company?.permissions,
            payment,
            {
              canDisplayLoyaltyInfoOnReceipt,
              canUseCustomReceipt,
            },
            customReceiptSettings.get(payment.locationId)?.showLoyalty,
          )
        : undefined
    }

    this.setState({
      expanded,
      refundPayment,
      originalSalePayment,
      loyaltyData,
      errorMessage: undefined,
    })
  }

  get externalSource(): string {
    const defaultSourceName = BLAZE_COMPANY
    const externalSourceName = this.props.payment.externalSourceName || defaultSourceName
    const externalOrderIdString =
      this.props.payment.externalOrderId !== null && externalSourceName !== defaultSourceName
        ? ` (${this.props.payment.externalOrderId})`
        : ''

    return `${externalSourceName}${externalOrderIdString}`
  }

  get expandedElement(): JSX.Element | undefined {
    if (!this.state.expanded) {
      return undefined
    } else if (this.state.isLoading) {
      return <Skeleton active />
    } else {
      const { canRefund, canEditCustomer, currentEmployeeId, payment } = this.props

      const company = this.props.currentCompanyStore!.company!
      const canEditSales = this.props.currentUserStore!.hasPermissionAtEntity(
        PermissionNames.CAN_EDIT_SALES,
        company!.id,
      )

      return (
        <ExpandablePaymentDetails
          onUpdate={() => this.onUpdate()}
          payment={payment}
          canRefundSales={canRefund}
          canEditSales={canEditSales}
          canEditCustomer={canEditCustomer}
          locationId={payment.locationId}
          currentEmployeeId={currentEmployeeId}
          refundPayment={this.state.refundPayment || undefined}
          originalSalePayment={this.state.originalSalePayment || undefined}
          loyaltyData={this.state.loyaltyData}
          paymentOptions={this.props.paymentOptions}
        />
      )
    }
  }

  render() {
    const payment = this.props.payment

    let statusElement: JSX.Element | undefined
    if (payment.status === 'completed') {
      statusElement = <span className='badge badge-success'>Completed</span>
    } else if (payment.status === 'in progress') {
      statusElement = <span className='badge badge-warning'>In progress</span>
    } else if (payment.status === 'cancelled') {
      statusElement = <span className='badge badge-danger'>Cancelled</span>
    } else if (payment.status === 'failed') {
      statusElement = <span className='badge badge-danger'>Failed</span>
    } else if (payment.status === 'pending') {
      statusElement = <span className='badge badge-light'>Pending</span>
    } else if (payment.status === 'refunded') {
      statusElement = <span className='badge badge-danger'>Refunded</span>
    }

    // This element shows whether all products in cart have been scanned in
    let barcodeElement: JSX.Element | undefined
    const productLineCount = payment.paymentLines.filter((p) => p.productId).length
    const barcodeLineCount = payment.paymentLines.filter((p) => p.productId && p.barcode).length
    if (productLineCount > 0 && productLineCount === barcodeLineCount) {
      barcodeElement = <span className='fa fa-barcode' />
    } else if (barcodeLineCount > 0) {
      barcodeElement = <span className='text-muted'>partial</span>
    }
    const fullNotes = getFullNotes(false, payment.notes!, payment.paymentQueueNotes!)

    const baseElement = (
      <tr onClick={this.onClick} style={{ cursor: 'pointer' }}>
        <td className='text-muted'>{payment.customId}</td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {moment(payment.completeDate || payment.createDate).format('MMM D, YYYY hh:mm a')}
        </td>
        <td className='text-muted'>{this.externalSource}</td>
        <td className='text-muted'>{payment.deviceName}</td>
        <td className='text-muted'>
          {payment.transactions.map((trans) => {
            return (
              <span key={trans.id} className='mr-2'>
                {paymentNameFormatter(trans.paymentTypeId)}
              </span>
            )
          })}
        </td>
        <td className='text-muted'>{payment.employeeName || '-'}</td>
        <td className='text-muted'>{payment.customerName || '-'}</td>
        <td className='text-muted'>
          {!!fullNotes && (
            <Tooltip title={fullNotes} overlayClassName={'gr-sales-notes'}>
              <Icon type='info-circle' />
            </Tooltip>
          )}
        </td>
        <td className='text-right'>{barcodeElement}</td>
        <td className='text-right'>
          <b>
            {payment.refundPaymentId ? (
              <Currency className='text-danger' centValue={payment.total} />
            ) : (
              <Currency centValue={payment.total} />
            )}
          </b>
        </td>
        <td className='text-right'>
          {!!payment.wooCommerceOrderId && (
            <span className='badge badge-light mr-2'>WooCommerce {payment.wooCommerceOrderId}</span>
          )}
          {transactionsIncomplete(payment) && payment.status !== PaymentStatus.CANCELLED && (
            <>
              <Tooltip
                title={
                  <>
                    <div>There are incomplete terminal transactions.</div>
                    <div>
                      We recommend processing the remaining transactions or cancelling this sale.
                    </div>
                  </>
                }
              >
                <span className='fa fa-exclamation-triangle text-muted' />
              </Tooltip>
            </>
          )}
          {statusElement}
        </td>
      </tr>
    )

    return (
      <>
        {baseElement}
        {this.state.expanded ? this.expandedElement : undefined}
      </>
    )
  }
}
